import * as Sentry from '@sentry/nuxt'

type AuthEndpoint = '/api/auth/session'
  | '/api/auth/login'
  | '/api/auth/logout'
  | '/api/auth/refresh'
  | '/api/auth/mfa/challenge'
  | '/api/auth/password/challenge'

type AuthFetchOptions = {
  method?: 'GET' | 'POST'
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body?: Record<string, any>
  headers?: Record<string, string>
}

interface LoginResponseWithChallenges {
  isAuthenticated?: boolean
  user?: AuthenticatedUser
  claims?: OidcJwtPayload
  expiresAt?: number
  challengeName?: 'SOFTWARE_TOKEN_MFA' | 'NEW_PASSWORD_REQUIRED'
  session?: string
  username?: string
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createNormalizedError = (error: any): Error => {
  const message = error?.data?.statusMessage
    ?? error?.data?.message
    ?? error?.message
    ?? 'Unexpected authentication error'

  const wrapped = new Error(message)
  if (error?.data?.statusCode || error?.statusCode) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(wrapped as any).statusCode = error?.data?.statusCode ?? error?.statusCode
  }
  return wrapped
}

export const useAuth = () => {
  const router = useRouter()

  const user = useState<AuthenticatedUser | null>('auth:user', () => null)
  const userClaims = useState<OidcJwtPayload | null>('auth:claims', () => null)
  const isAuthenticated = useState<boolean>('auth:isAuthenticated', () => false)
  const isLoading = useState<boolean>('auth:isLoading', () => false)
  const sessionExpiresAt = useState<number | null>('auth:expiresAt', () => null)

  // MFA challenge state
  const mfaRequired = useState<boolean>('auth:mfaRequired', () => false)
  const mfaSession = useState<string | null>('auth:mfaSession', () => null)
  const mfaUsername = useState<string | null>('auth:mfaUsername', () => null)

  // New password challenge state (Keycloak UPDATE_PASSWORD required action)
  const passwordChangeRequired = useState<boolean>('auth:passwordChangeRequired', () => false)
  const passwordChallengeSession = useState<string | null>('auth:passwordChallengeSession', () => null)
  const passwordChallengeUsername = useState<string | null>('auth:passwordChallengeUsername', () => null)
  const passwordChallengePassword = useState<string | null>('auth:passwordChallengePassword', () => null)

  // MFA challenge — password kept in memory until OTP is submitted (Keycloak has no session token)
  const mfaPassword = useState<string | null>('auth:mfaPassword', () => null)

  const applySession = (session: AuthSessionResponse | null): boolean => {
    if (!session || !session.isAuthenticated || !session.user) {
      user.value = null
      userClaims.value = null
      isAuthenticated.value = false
      sessionExpiresAt.value = null
      return false
    }

    user.value = session.user
    userClaims.value = session.claims ?? null
    isAuthenticated.value = true
    sessionExpiresAt.value = session.expiresAt ?? null
    return true
  }

  const callAuthEndpoint = async <T = AuthSessionResponse>(
    endpoint: AuthEndpoint,
    options?: AuthFetchOptions
  ): Promise<T> => {
    try {
      return await $fetch(endpoint, {
        credentials: 'include',
        ...options,
        headers: {
          ...(options?.headers ?? {})
        }
      }) as T
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // Only capture unexpected errors (not 401/403 which are user errors)
      const statusCode = error?.data?.statusCode ?? error?.statusCode
      const isUserError = statusCode === 401 || statusCode === 403

      if (!isUserError) {
        Sentry.captureException(error, {
          tags: {
            endpoint,
            feature: 'auth'
          }
        })
      }

      throw createNormalizedError(error)
    }
  }

  const checkAuthStatus = async (): Promise<boolean> => {
    try {
      isLoading.value = true
      const session = await callAuthEndpoint<AuthSessionResponse>('/api/auth/session')

      // Handle unauthenticated response (not an error)
      if (!session.isAuthenticated) {
        applySession(null)
        return false
      }

      return applySession(session)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((error as any).statusCode === 401) {
        applySession(null)
        return false
      }

      applySession(null)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const clearMFAChallengeState = () => {
    mfaRequired.value = false
    mfaSession.value = null
    mfaUsername.value = null
    mfaPassword.value = null
  }

  const clearPasswordChallengeState = () => {
    passwordChangeRequired.value = false
    passwordChallengeSession.value = null
    passwordChallengeUsername.value = null
    passwordChallengePassword.value = null
  }

  const clearChallengeState = () => {
    clearMFAChallengeState()
    clearPasswordChallengeState()
  }

  /**
   * Clears ALL auth state - used when switching modes or signing out.
   * This ensures no stale state leaks between demo and real auth.
   */
  const clearAuthState = () => {
    user.value = null
    userClaims.value = null
    isAuthenticated.value = false
    isLoading.value = false
    sessionExpiresAt.value = null
    clearChallengeState()
  }

  const handleSignIn = async (email: string, password: string): Promise<boolean> => {
    try {
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'User attempting login',
        level: 'info'
      })

      isLoading.value = true
      clearChallengeState()

      const response = await callAuthEndpoint<LoginResponseWithChallenges>('/api/auth/login', {
        method: 'POST',
        body: { email, password }
      })

      // Check if MFA challenge is required
      if (response.challengeName === 'SOFTWARE_TOKEN_MFA') {
        mfaRequired.value = true
        mfaSession.value = response.session ?? null
        mfaUsername.value = response.username || email
        mfaPassword.value = password
        return false
      }

      // Check if user must set a new password before completing login
      if (response.challengeName === 'NEW_PASSWORD_REQUIRED') {
        passwordChangeRequired.value = true
        passwordChallengeSession.value = response.session ?? null
        passwordChallengeUsername.value = response.username || email
        passwordChallengePassword.value = password
        return false
      }

      // No MFA, apply session directly
      applySession(response as AuthSessionResponse)
      await router.push('/')
      return true
    } catch (error) {
      applySession(null)
      clearChallengeState()
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const completeMFAChallenge = async (code: string): Promise<void> => {
    if (!mfaUsername.value || !mfaPassword.value) {
      throw new Error('No MFA challenge pending')
    }

    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'User completing MFA challenge',
      level: 'info'
    })

    isLoading.value = true

    try {
      const session = await callAuthEndpoint<AuthSessionResponse>('/api/auth/mfa/challenge', {
        method: 'POST',
        body: {
          username: mfaUsername.value,
          password: mfaPassword.value,
          code
        }
      })

      clearChallengeState()
      applySession(session)
      await router.push('/')
    } finally {
      // Don't clear MFA state on error so user can retry
      isLoading.value = false
    }
  }

  const cancelMFAChallenge = () => {
    clearMFAChallengeState()
  }

  const completeNewPasswordChallenge = async (newPassword: string): Promise<boolean> => {
    if (!passwordChallengeUsername.value || !passwordChallengePassword.value) {
      throw new Error('No password challenge pending')
    }

    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'User completing new password challenge',
      level: 'info'
    })

    isLoading.value = true

    try {
      const response = await callAuthEndpoint<LoginResponseWithChallenges>('/api/auth/password/challenge', {
        method: 'POST',
        body: {
          username: passwordChallengeUsername.value,
          currentPassword: passwordChallengePassword.value,
          newPassword
        }
      })

      if (response.challengeName === 'SOFTWARE_TOKEN_MFA') {
        clearPasswordChallengeState()
        mfaRequired.value = true
        mfaSession.value = response.session ?? null
        mfaUsername.value = response.username || passwordChallengeUsername.value
        mfaPassword.value = newPassword
        return false
      }

      clearChallengeState()
      applySession(response as AuthSessionResponse)
      await router.push('/')
      return true
    } finally {
      // Keep password challenge state on error so the user can retry
      isLoading.value = false
    }
  }

  const cancelPasswordChallenge = () => {
    clearPasswordChallengeState()
  }

  const handleSignOut = async (): Promise<void> => {
    const { isDemoMode, exitDemoAndRedirect } = useDemo()

    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'User logging out',
      level: 'info'
    })

    // If in demo mode, just exit demo and redirect
    if (isDemoMode.value) {
      await exitDemoAndRedirect()
      return
    }

    try {
      isLoading.value = true
      await callAuthEndpoint('/api/auth/logout', { method: 'POST' })
    } finally {
      applySession(null)
      isLoading.value = false
    }

    await router.push('/login')
  }

  const refreshSession = async (): Promise<boolean> => {
    try {
      const session = await callAuthEndpoint<AuthSessionResponse>('/api/auth/refresh', {
        method: 'POST'
      })
      return applySession(session)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((error as any).statusCode === 401) {
        applySession(null)
        return false
      }

      throw error
    }
  }

  const getUserDisplayName = (): string => {
    if (!user.value) return ''

    return user.value.name
      || `${user.value.givenName || ''} ${user.value.familyName || ''}`.trim()
      || user.value.email
      || user.value.username
  }

  return {
    user: readonly(user),
    userClaims: readonly(userClaims),
    isAuthenticated: readonly(isAuthenticated),
    isLoading: readonly(isLoading),
    sessionExpiresAt: readonly(sessionExpiresAt),

    // MFA state
    mfaRequired: readonly(mfaRequired),
    mfaUsername: readonly(mfaUsername),
    passwordChangeRequired: readonly(passwordChangeRequired),
    passwordChallengeUsername: readonly(passwordChallengeUsername),

    checkAuthStatus,
    handleSignIn,
    handleSignOut,
    refreshSession,
    getUserDisplayName,
    clearAuthState,

    // MFA methods
    completeMFAChallenge,
    cancelMFAChallenge,
    completeNewPasswordChallenge,
    cancelPasswordChallenge
  }
}
